<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  teams: {
    type: Array,
    default: () => [],
  },
  variant: {
    type: String,
    default: 'side',
    validator: value => ['side', 'compact'].includes(value),
  },
});

const store = useStore();
const { t } = useI18n();

const selectedTeamId = ref('none');
const isResolving = ref(false);
const isTransferring = ref(false);

const currentTeam = computed(() => props.conversation?.meta?.team || null);
const hasConversation = computed(() => Boolean(props.conversation?.id));
const isResolved = computed(
  () => props.conversation?.status === wootConstants.STATUS_TYPE.RESOLVED
);

watch(
  () => props.conversation?.id,
  () => {
    selectedTeamId.value = currentTeam.value?.id || 'none';
  },
  { immediate: true }
);

const resolveConversation = async () => {
  if (!hasConversation.value || isResolved.value) return;
  isResolving.value = true;
  try {
    await store.dispatch('toggleStatus', {
      conversationId: props.conversation.id,
      status: wootConstants.STATUS_TYPE.RESOLVED,
    });
    useAlert(t('ESSENTIAL.ACTIONS.RESOLVE_SUCCESS'));
  } finally {
    isResolving.value = false;
  }
};

const transferConversation = async () => {
  if (!hasConversation.value) return;

  const teamId = selectedTeamId.value === 'none' ? null : selectedTeamId.value;
  isTransferring.value = true;
  try {
    await store.dispatch('assignTeam', {
      conversationId: props.conversation.id,
      teamId,
      skipSync: true,
    });
    useAlert(t('ESSENTIAL.ACTIONS.TRANSFER_SUCCESS'));
  } finally {
    isTransferring.value = false;
  }
};
</script>

<template>
  <div
    v-if="variant === 'compact'"
    class="flex flex-col flex-shrink-0 gap-3 border-b border-n-weak bg-n-surface-1 px-3 py-3 lg:hidden"
  >
    <div class="grid grid-cols-[1fr_auto] gap-2">
      <select
        v-model="selectedTeamId"
        class="w-full !mb-0 text-sm"
        :disabled="!hasConversation"
      >
        <option value="none">
          {{ $t('ESSENTIAL.FILTERS.NO_TEAM') }}
        </option>
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>

      <Button
        slate
        faded
        sm
        icon="i-lucide-user-round-plus"
        :disabled="!hasConversation"
        :is-loading="isTransferring"
        :label="$t('ESSENTIAL.ACTIONS.TRANSFER')"
        @click="transferConversation"
      />
    </div>

    <Button
      teal
      sm
      icon="i-lucide-circle-check"
      :disabled="!hasConversation || isResolved"
      :is-loading="isResolving"
      :label="$t('ESSENTIAL.ACTIONS.RESOLVE')"
      @click="resolveConversation"
    />
  </div>

  <aside
    v-else
    class="hidden lg:flex flex-col flex-shrink-0 w-[280px] border-l border-n-weak bg-n-surface-1 p-5 gap-5"
  >
    <section class="flex flex-col gap-3">
      <h2 class="m-0 text-base font-medium text-n-slate-12">
        {{ $t('ESSENTIAL.ACTIONS.TITLE') }}
      </h2>

      <Button
        teal
        icon="i-lucide-circle-check"
        :disabled="!hasConversation || isResolved"
        :is-loading="isResolving"
        :label="$t('ESSENTIAL.ACTIONS.RESOLVE')"
        @click="resolveConversation"
      />
    </section>

    <section class="flex flex-col gap-3 pt-5 border-t border-n-weak">
      <label class="flex flex-col gap-1 mb-0">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('ESSENTIAL.ACTIONS.TRANSFER_TO') }}
        </span>
        <select
          v-model="selectedTeamId"
          class="w-full !mb-0 text-sm"
          :disabled="!hasConversation"
        >
          <option value="none">
            {{ $t('ESSENTIAL.FILTERS.NO_TEAM') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
      </label>

      <Button
        slate
        faded
        icon="i-lucide-user-round-plus"
        :disabled="!hasConversation"
        :is-loading="isTransferring"
        :label="$t('ESSENTIAL.ACTIONS.TRANSFER')"
        @click="transferConversation"
      />
    </section>

    <section class="flex flex-col gap-2 pt-5 border-t border-n-weak">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('ESSENTIAL.ACTIONS.CURRENT_TEAM') }}
      </span>
      <span
        class="inline-flex w-fit max-w-full items-center rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11"
      >
        {{ currentTeam?.name || $t('ESSENTIAL.FILTERS.NO_TEAM') }}
      </span>
    </section>
  </aside>
</template>
